/**
 * PATH: packages/ui/src/kanban/components/EmailThreadView.tsx
 * FILE_ID: UI-KANBAN-EMAIL-VIEW-001
 * PURPOSE: Self-contained email-thread rendering surface for use INSIDE
 *          a card-detail drawer tab (and anywhere else that needs to
 *          show the thread for a card without owning a whole drawer).
 * WHY:
 *   Operator complaint (2026-05-08): "Card opens as an email.. and
 *   that too a half ass email instead it shoudl open in the right
 *   drawer which has width changeability, with proper card
 *   functionality and tabs where you can view the email leave the
 *   notes etc.. use first principles."
 *
 *   Until today the kanban surface had TWO drawers: a general
 *   CardDetailDrawer (notes, due dates, lane moves) and an
 *   EmailThreadDrawer (subject, from/to, body iframe). Email-backed
 *   cards opened the latter — a panel that could only show the
 *   email thread with no notes/files/audit-log access. Switching
 *   between the two surfaces required closing one and clicking
 *   different state.
 *
 *   The fix is to merge: CardDetailDrawer grows a tab strip
 *   (Card · Email · Files), and the Email tab embeds THIS view —
 *   the same iframe-sandboxed thread render that EmailThreadDrawer
 *   shipped, just in a re-usable form so the merge doesn't lose
 *   the thread-switcher / load-full-thread / Email Hub deep-link
 *   affordances operators were relying on.
 *
 * FLOW:
 *   ┌─────────────┐    ┌───────────────────┐    ┌──────────────────┐
 *   │ card.       │ →  │ adapter.getEmail  │ →  │ iframe srcDoc    │
 *   │ primary_    │    │ adapter.getThread │    │ (sandboxed)      │
 *   │ email       │    │ (lazy on demand)  │    │ + thread switch  │
 *   └─────────────┘    └───────────────────┘    └──────────────────┘
 *
 * SAFETY: body_html is rendered through the shared `<EmailHtmlBody>`,
 * which runs the SAME `sanitizeEmailHtml` (DOMPurify + cid-rewrite)
 * pass the Email Hub reader uses across every tenant. The previous
 * `<iframe srcDoc sandbox>` isolated email CSS but never cid-rewrote
 * inline images (so signatures/screenshots rendered broken) and the
 * typography diverged from the hub. DOMPurify is the project-wide
 * standard for rendering external `body_html`; matching it gives the
 * operator-requested hub parity without re-introducing the broken
 * inline-image surface.
 *
 * RECOVERY: inline-image URLs are SIGNED and short-lived (~15 min, and
 * invalidated on re-auth). When one fails to load (expired signature /
 * transient 401 after a session blip) this view refetches the email so
 * the backend re-signs the URLs — the same self-heal the Email Hub
 * reader has via `useInlineImageRecovery`. A fully signed-out session
 * (the "Quotech connection lost … 401" banner) still needs Reconnect;
 * no re-sign succeeds without a valid session, so recovery gives up
 * after MAX_INLINE_RECOVERIES instead of spinning the backend.
 */

import { useEffect, useRef, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Mail,
  MailX,
  RotateCcw,
} from 'lucide-react'

import { EmailHtmlBody } from '../../components/email/EmailHtmlBody'
import { CalendarInviteCard } from '../../components/email/CalendarInviteCard'
import { EmailThreadReplyBar } from './EmailThreadReplyBar'
import type { KanbanEmailAdapter, KanbanEmailPreview } from '../api'

/** Path fragment present in every signed inline-image URL
 *  (`/api/emails/inline/<id>?sig=…`). We only auto-recover broken
 *  `<img>` tags pointing at THAT endpoint — a dead logo on the sender's
 *  CDN or a tracking pixel won't be fixed by refetching, so those keep
 *  the browser's native broken-image glyph. */
const INLINE_URL_MARKER = '/inline/'
/** Absorb a burst of inline failures from one render frame (a long
 *  signature chain can fail 10+ images at once) into a single refetch. */
const INLINE_RECOVERY_DEBOUNCE_MS = 250
/** Cap refetches per email so a structurally-missing attachment (vs a
 *  one-shot expired signature) can't spin the backend. */
const MAX_INLINE_RECOVERIES = 2

export interface EmailThreadViewProps {
  /** ID of the email to render (typically `card.primary_email`).
   *  When null the view shows an "(no email linked)" empty state —
   *  useful for the Email tab on manual cards so operators get a
   *  clear "this card has no email" signal instead of a blank
   *  panel. */
  emailId: string | null
  /** Tenant-supplied fetch adapter. Reuse the same adapter the
   *  rest of the kanban surface uses — KanbanBoardView already
   *  has it on `hooks.emailAdapter`. */
  adapter: KanbanEmailAdapter
  /** Optional fallback subject — shown in the header before the
   *  email payload arrives. Pass the card's title so the panel
   *  isn't empty for the first ~200ms after the operator opens
   *  the tab. */
  fallbackSubject?: string
  /** ARC-1: the card this thread is rendered inside. Threaded into the
   *  inline composer so a message sent from the drawer links back to
   *  the card (`linked_card_ids` on the send payload). */
  cardId?: string | null
  /** ARC-1 MR2: notified after any inline composer send (reply or new).
   *  The card surface uses this to invalidate its card / linked-email
   *  queries so a newly-linked sent email appears without a reload. */
  onComposerSent?: () => void
}

/**
 * Sandbox-friendly email thread view. Mounts on first render —
 * caller is expected to keep this unmounted while the Email tab
 * is hidden (so the iframe + adapter request only fires when the
 * operator explicitly opens the tab; saves a network round-trip
 * on every card click).
 */
export function EmailThreadView({
  emailId,
  adapter,
  fallbackSubject,
  cardId,
  onComposerSent,
}: EmailThreadViewProps) {
  const [head, setHead] = useState<KanbanEmailPreview | null>(null)
  const [thread, setThread] = useState<KanbanEmailPreview[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [threadLoading, setThreadLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeIdx, setActiveIdx] = useState(0)
  // BUG-2026-06-29-KANBAN-EMAIL-LOAD-ERROR-COPY — bump to re-run the head
  // fetch when the operator hits Retry on the error state.
  const [reloadKey, setReloadKey] = useState(0)

  // Inline-image recovery — signed inline-image URLs expire (~15 min)
  // and are invalidated when the session re-authenticates. The Email
  // Hub reader self-heals a broken inline image by refetching the email
  // (which re-signs every `inline_url`); the card drawer previously did
  // NOT, so an expired signature left a permanent broken-image box even
  // though a reload would have fixed it. Operator (2026-05-31): "why
  // did the image not load?" — the email text was already in hand but
  // the inline logo's signed URL had failed (session signed out / 401).
  // We attach the SAME capture-phase `<img error>` → debounced-refetch
  // contract here. Re-implemented locally (not imported from
  // @quotech/email-hub) so @quotech/ui stays free of an email-hub
  // dependency — that edge would be a cycle (email-hub depends on ui).
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const inlineRetriesRef = useRef<{ emailId: string | null; count: number }>({
    emailId: null,
    count: 0,
  })
  if (inlineRetriesRef.current.emailId !== emailId) {
    inlineRetriesRef.current = { emailId, count: 0 }
  }

  // Reset internal state when the email id changes (operator
  // opened a different card). Without this you'd briefly see the
  // previous email's body while the new fetch is in flight.
  useEffect(() => {
    setHead(null)
    setThread(null)
    setError(null)
    setActiveIdx(0)
  }, [emailId])

  // Fetch the head email on mount / id change.
  useEffect(() => {
    if (!emailId) return
    let cancelled = false
    setLoading(true)
    adapter
      .getEmail(emailId, { cardId })
      .then((res) => {
        if (cancelled) return
        setHead(res)
        setError(null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load email')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [emailId, adapter, reloadKey, cardId])

  // Capture-phase `<img error>` listener on the body container. When a
  // signed inline-image URL fails (expired signature / transient 401)
  // we debounce, then refetch the email so the backend re-issues fresh
  // signatures and the body re-renders with working URLs. Depends on
  // `head`/`thread` so it (re-)binds once the body actually mounts —
  // the container is only rendered after the first fetch resolves, so a
  // deps list of just [emailId] would attach the listener while
  // `bodyRef.current` is still null. Bounded by MAX_INLINE_RECOVERIES.
  useEffect(() => {
    const root = bodyRef.current
    if (!root || !emailId) return
    let cancelled = false
    let pendingTimer: number | null = null
    let sawInlineFailure = false
    const onError = (e: Event) => {
      const target = e.target
      if (!(target instanceof HTMLImageElement)) return
      const src = target.currentSrc || target.src || ''
      if (!src.includes(INLINE_URL_MARKER)) return
      sawInlineFailure = true
      if (pendingTimer !== null) return
      pendingTimer = window.setTimeout(() => {
        pendingTimer = null
        if (!sawInlineFailure) return
        sawInlineFailure = false
        const slot = inlineRetriesRef.current
        if (slot.emailId !== emailId) return
        if (slot.count >= MAX_INLINE_RECOVERIES) return
        slot.count += 1
        adapter
          .getEmail(emailId, { cardId })
          .then((res) => {
            if (!cancelled) setHead(res)
          })
          .catch(() => {
            /* best-effort — leave the broken glyph if re-sign fails
               (e.g. the session is fully signed out → needs Reconnect) */
          })
        if (thread) {
          adapter
            .getThread(emailId, { cardId })
            .then((t) => {
              if (!cancelled) setThread(t)
            })
            .catch(() => {})
        }
      }, INLINE_RECOVERY_DEBOUNCE_MS)
    }
    // `error` doesn't bubble but DOES fire in the capture phase on every
    // ancestor, so `capture: true` here catches all descendant <img>.
    root.addEventListener('error', onError, true)
    return () => {
      cancelled = true
      root.removeEventListener('error', onError, true)
      if (pendingTimer !== null) window.clearTimeout(pendingTimer)
    }
  }, [emailId, adapter, head, thread, cardId])

  // No email linked → empty state. Manual cards never have a
  // primary_email; operators need a clear signal that this isn't
  // a bug — the Email tab simply doesn't apply to this card.
  if (!emailId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-12 text-center text-neutral-500 dark:text-zinc-400">
        <Mail className="h-8 w-8 opacity-40" aria-hidden />
        <p className="text-sm font-medium">No email linked to this card</p>
        <p className="max-w-[280px] text-[11.5px] leading-snug">
          Drag an email from the right rail onto this card to allocate it,
          or open the Card tab to leave a note.
        </p>
      </div>
    )
  }

  const showThreadToggle = (head?.thread_count ?? 1) > 1
  const messages = thread ?? (head ? [head] : [])
  const active = messages[activeIdx] ?? head

  const onLoadFullThread = async () => {
    if (thread || !emailId) return
    setThreadLoading(true)
    try {
      const t = await adapter.getThread(emailId, { cardId })
      setThread(t)
      const idx = t.findIndex((m) => m.id === emailId)
      if (idx >= 0) setActiveIdx(idx)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Thread fetch failed')
    } finally {
      setThreadLoading(false)
    }
  }

  // ARC-1 MR3 — the email changed server-side (tag added/removed, or a
  // reply landed). Refetch the head (re-signs inline URLs too) and, when
  // the full thread is already loaded, refetch it so the change appears
  // without the operator leaving the card.
  const onEmailMutated = () => {
    setReloadKey((k) => k + 1)
    if (thread) {
      adapter
        .getThread(emailId, { cardId })
        .then((t) => setThread(t))
        .catch(() => {
          /* best-effort — the head refetch above still lands */
        })
    }
  }

  // ARC-1: an inline reply was just sent from the drawer.
  const onInlineSent = () => {
    onEmailMutated()
    // Card surface invalidates its own queries (linked emails list) so a
    // newly sent + card-linked message shows up without a reload.
    onComposerSent?.()
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Email metadata header — subject, from/to, received-at.
          Lives inside the tab pane so the surrounding drawer keeps
          its own card title in the drawer header (the email
          subject and the card title diverge for cards renamed by
          the operator after import). */}
      <header className="border-b border-neutral-200 px-4 py-2 dark:border-zinc-800">
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          <Mail className="h-3 w-3" /> Email
          {showThreadToggle && (
            <span className="ml-1 rounded-sm bg-neutral-100 px-1 py-0.5 text-[10px] dark:bg-zinc-800">
              {messages.length}/{head?.thread_count ?? '?'} in thread
            </span>
          )}
        </div>
        <h3 className="mt-0.5 truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          {active?.subject || head?.subject || fallbackSubject || 'Loading…'}
        </h3>
        {active && (
          <div className="mt-1 space-y-0.5 text-xs text-neutral-500 dark:text-zinc-400">
            <div className="truncate">
              <span className="font-medium text-neutral-700 dark:text-zinc-200">
                {active.from}
              </span>
              {active.to && <> · to {active.to}</>}
              {active.received_at && (
                <> · {new Date(active.received_at).toLocaleString()}</>
              )}
            </div>
            {/* BUG-2026-07-02-KANBAN-EMAIL-RECIPIENTS — the drawer header only
                showed From + To, so Cc/Bcc recipients on a card's email were
                invisible (the standalone Email Hub reader shows them). Render
                Cc when present, and Bcc ONLY when present (a normal recipient
                never sees a Bcc line). Each truncates on its own row so a long
                recipient list stays reachable via the native title tooltip. */}
            {active.cc && (
              <div className="truncate" title={active.cc}>
                <span className="font-medium text-neutral-600 dark:text-zinc-300">Cc:</span>{' '}
                {active.cc}
              </div>
            )}
            {active.bcc && (
              <div className="truncate" title={active.bcc}>
                <span className="font-medium text-neutral-600 dark:text-zinc-300">Bcc:</span>{' '}
                {active.bcc}
              </div>
            )}
          </div>
        )}
        {/* ARC-1 MR3 — org tag chips for the open message (the Email
            Hub's own EmailTagChips, injected via the adapter). Fixes
            "missing tags in drawers": tags now render + edit in place;
            onChanged refetches so chips repaint from the server. */}
        {active && adapter.TagChips && (
          <div className="mt-1.5" data-testid="email-thread-tags">
            <adapter.TagChips
              emailId={active.id}
              tags={active.tags}
              size="xs"
              onChanged={onEmailMutated}
            />
          </div>
        )}
      </header>

      {/* Thread switcher — only when more than one message in the
          thread. The "Load full thread" button defers the heavier
          getThread call until the operator actually wants it. */}
      {showThreadToggle && (
        <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-3 py-1.5 dark:border-zinc-800 dark:bg-zinc-900/60">
          <button
            type="button"
            onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
            disabled={activeIdx === 0}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-neutral-600 hover:bg-neutral-200 disabled:opacity-40 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Newer
          </button>
          <button
            type="button"
            onClick={() => void onLoadFullThread()}
            className="rounded px-2 py-0.5 text-[11px] font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/40"
          >
            {thread
              ? `Showing ${activeIdx + 1} of ${messages.length}`
              : threadLoading
                ? 'Loading thread…'
                : 'Load full thread'}
          </button>
          <button
            type="button"
            onClick={() =>
              setActiveIdx((i) => Math.min(messages.length - 1, i + 1))
            }
            disabled={activeIdx >= messages.length - 1}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-neutral-600 hover:bg-neutral-200 disabled:opacity-40 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            Older <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Body — rendered through the SHARED `<EmailHtmlBody>`, the exact
          same `sanitizeEmailHtml` + `.prose` pipeline the Email Hub
          reader uses. This is the operator-requested parity: inline
          `cid:` images resolve (via `active.attachments`), external imgs
          are hardened, and dark-mode typography matches the hub. We
          dropped the old `<iframe srcDoc>` (it never cid-rewrote, so
          inline signatures/screenshots rendered as broken boxes). The
          card frame + scroll stay on the parent so the body reads like a
          mailbox message inside the drawer tab. */}
      <div className="flex-1 overflow-y-auto bg-neutral-50 dark:bg-zinc-950">
        {loading && (
          <div className="p-6 text-sm text-neutral-500">Loading email…</div>
        )}
        {error && (
          // BUG-2026-06-29-KANBAN-EMAIL-LOAD-ERROR-COPY — lead with a
          // friendly headline + Retry instead of dumping the raw adapter
          // error (e.g. "Failed to fetch", a 403 body). The technical
          // detail stays available below in muted text for support.
          <div className="m-4 flex flex-col items-center gap-2 rounded-lg border border-neutral-200 bg-white p-6 text-center dark:border-zinc-800 dark:bg-zinc-900">
            <MailX className="h-8 w-8 text-neutral-300 dark:text-zinc-600" aria-hidden />
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Couldn&apos;t load this email
            </p>
            <p className="max-w-[320px] text-[11.5px] leading-snug text-neutral-500 dark:text-zinc-400">
              {/404|not found|forbidden|403/i.test(error)
                ? 'You may not have access to this message, or it was removed from the card.'
                : 'A temporary network or mailbox issue may be blocking the load. Try again, or reconnect your mailbox in Settings if it keeps failing.'}
            </p>
            {!/404|not found|forbidden|403/i.test(error) && (
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  setReloadKey((k) => k + 1)
                }}
                className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-2.5 py-1 text-[12px] font-medium text-neutral-700 hover:bg-neutral-50 dark:border-zinc-700 dark:text-neutral-200 dark:hover:bg-zinc-800"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Retry
              </button>
            )}
            <p className="mt-1 max-w-[320px] break-words text-[10px] text-neutral-400 dark:text-zinc-600">
              {error}
            </p>
          </div>
        )}
        {!loading && active && (
          <div className="p-3">
            {/* Calendar meeting-invite / cancellation banner — shown
                above the body when the message carries an
                EmailCalendarEvent sidecar (Outlook Graph
                meetingMessageType / parsed .ics). Operator 2026-06-07:
                "Was this a cancellation of meeting? … I should be able
                to see the calendar here." Same banner the standalone
                Email Hub reader renders; ported into @quotech/ui so the
                card drawer can show it without an email-hub dependency
                (that edge is a cycle). */}
            {active.calendar_event && (
              <CalendarInviteCard
                event={active.calendar_event}
                className="mb-3"
              />
            )}
            {active.body_html || active.body_preview ? (
              <EmailHtmlBody
                bodyRef={bodyRef}
                html={active.body_html}
                text={active.body_preview}
                attachments={active.attachments}
                className="rounded-lg border border-neutral-200 bg-white p-4 text-[13px] leading-relaxed dark:border-zinc-800 dark:bg-zinc-900"
              />
            ) : (
              <div className="rounded-lg border border-dashed border-neutral-200 bg-white p-6 text-center text-[12px] text-neutral-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
                (empty body)
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reply actions + inline composer (ARC-1). The bar owns the
          Reply / Reply-all / Forward affordances: with an injected
          `adapter.InlineComposer` they expand a full compose form in
          place (no navigation — the redirect the client reported);
          otherwise they fall back to `buildComposeUrl` deep-links. */}
      <EmailThreadReplyBar
        emailId={emailId}
        adapter={adapter}
        attachmentCount={active?.attachment_count}
        cardId={cardId}
        onSent={onInlineSent}
      />
    </div>
  )
}
